import {
  DOCUMENT,
  Injectable,
  NgZone,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { AuthService } from '../auth/auth.service';
import { environment } from '../../../environments/environment';
import type { CapabilitiesResponse, CapabilityState, CapabilityView } from './capabilities.model';

/** ADR 0031 section 3.1: "las refrescan cada unos 60 s". */
export const CAPABILITIES_REFRESH_MS = 60_000;
/** A session that gets these cannot read its capabilities: asking again would not help. */
const STOP_STATUSES = new Set([401, 403]);

function statusOf(err: unknown): number | null {
  const status = (err as { status?: unknown } | null)?.status;
  return typeof status === 'number' ? status : null;
}

/**
 * What CS says AD may show in this environment (ADR 0031 section 3): asked at login, when the
 * window gets the focus back and about every 60 s while the tab is visible. A key CS does not
 * publish, no session, or a session that never got an answer all read as DISABLED (ADR 0031
 * section 3.5). A failed refresh keeps the last good answer, with `failed()` raising the shell
 * notice while the latest request fails; a 401 or 403 stops the refreshes for the session; signing
 * out forgets the answer. Same failure rules as PFA. Roles stay apart: this never grants anything a
 * role does not.
 */
@Injectable({ providedIn: 'root' })
export class CapabilitiesService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);
  private readonly document = inject(DOCUMENT);
  private readonly zone = inject(NgZone);

  private readonly answer = signal<ReadonlyMap<string, CapabilityView> | null>(null);
  private readonly _settled = signal(false);
  private readonly _failed = signal(false);
  private readonly _revision = signal<number | null>(null);

  /** The first answer of this session arrived (or failed): guards can decide. */
  readonly settled = this._settled.asReadonly();
  /** The last request failed: the shell shows a non-blocking notice. */
  readonly failed = this._failed.asReadonly();
  readonly revision = this._revision.asReadonly();
  /** True while every capability-gated feature is closed for lack of an answer. */
  readonly unknown = computed(() => this.answer() === null);

  private sessionToken: string | null = null;
  private inFlight = false;
  /** A 401 or 403 answered: no more requests in this session. */
  private stopped = false;
  private waiters: (() => void)[] = [];

  /**
   * The session token. Read defensively: the Storybook pages that mount the shell double
   * AuthService with only the members they use; without `token` there is no session to ask for.
   */
  private sessionTokenNow(): string | null {
    return typeof this.auth.token === 'function' ? this.auth.token() : null;
  }

  constructor() {
    effect((onCleanup) => {
      const token = this.sessionTokenNow();
      untracked(() => this.syncSession(token));
      if (token === null) return;
      const view = this.document.defaultView;
      // Outside the Angular zone: a zone interval would keep the app from ever being stable.
      const timer = this.zone.runOutsideAngular(() =>
        setInterval(() => {
          if (this.document.visibilityState !== 'hidden') this.zone.run(() => this.refresh());
        }, CAPABILITIES_REFRESH_MS),
      );
      const onFocus = () => this.refresh();
      view?.addEventListener('focus', onFocus);
      onCleanup(() => {
        clearInterval(timer);
        view?.removeEventListener('focus', onFocus);
      });
    });
  }

  state(key: string): CapabilityState {
    return this.answer()?.get(key)?.state ?? 'DISABLED';
  }

  reason(key: string): string | null {
    return this.answer()?.get(key)?.reason ?? null;
  }

  /** Menus and tabs: hidden only while DISABLED; a PAUSED feature stays readable. */
  isVisible(key: string): boolean {
    return this.state(key) !== 'DISABLED';
  }

  /** Resolves once this session's first answer arrived or failed (at once without a session). */
  whenSettled(): Promise<void> {
    const token = this.sessionTokenNow();
    this.syncSession(token);
    if (token === null || this._settled()) return Promise.resolve();
    return new Promise((resolve) => this.waiters.push(resolve));
  }

  /** Asks CS again, unless a request is already on its way or there is no session. */
  refresh(): void {
    if (this.sessionToken === null || this.inFlight || this.stopped) return;
    const token = this.sessionToken;
    this.inFlight = true;
    this.http.get<CapabilitiesResponse>(`${environment.apiUrl}/admin/me/capabilities`).subscribe({
      next: (response) => {
        if (token !== this.sessionToken) return;
        this.inFlight = false;
        this.answer.set(new Map(response.capabilities.map((c) => [c.key, c])));
        this._revision.set(response.revision);
        this._failed.set(false);
        this.settle();
      },
      error: (err: unknown) => {
        if (token !== this.sessionToken) return;
        this.inFlight = false;
        const status = statusOf(err);
        if (status !== null && STOP_STATUSES.has(status)) this.stopped = true;
        // The last good answer (if any) stays: only a session that never got one is all DISABLED.
        this._failed.set(true);
        this.settle();
      },
    });
  }

  /** A new session (login, another user) starts from nothing; a logout forgets everything. */
  private syncSession(token: string | null): void {
    if (token === this.sessionToken) return;
    this.sessionToken = token;
    this.inFlight = false;
    this.answer.set(null);
    this._revision.set(null);
    this._settled.set(false);
    this._failed.set(false);
    this.stopped = false;
    if (token === null) {
      this.settle(false);
      return;
    }
    this.refresh();
  }

  private settle(markSettled = true): void {
    if (markSettled) this._settled.set(true);
    const waiters = this.waiters;
    this.waiters = [];
    waiters.forEach((resolve) => resolve());
  }
}
